import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useImageZoom } from '@/hooks/useImageZoom';
import type { ViewerProps } from '../FilePreviewModal.types';
import styles from '../FilePreviewModal.module.css';

export function ImageViewer({ data, contentType }: ViewerProps) {
  const zoom = useImageZoom();
  const [blobUrl, setBlobUrl] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Create blob URL in effect so cleanup/re-creation works in StrictMode double-mount
  useEffect(() => {
    const blob = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data, contentType]);

  // React binds onTouchMove as passive since React 17 — preventDefault is ignored.
  // Attach the listener manually with { passive: false } so pinch-zoom works.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const handler = zoom.handleNativeTouchMove;
    element.addEventListener('touchmove', handler, { passive: false });
    return () => {
      element.removeEventListener('touchmove', handler);
    };
  }, [zoom.handleNativeTouchMove]);

  if (!blobUrl) {
    return null;
  }

  const containerStyle = {
    '--image-cursor': zoom.isZoomed ? 'grab' : 'zoom-in',
    '--image-translate-x': `${zoom.state.translateX}px`,
    '--image-translate-y': `${zoom.state.translateY}px`,
    '--image-scale': zoom.state.scale,
  } as CSSProperties;

  return (
    <div
      ref={containerRef}
      className={styles.imageContainer}
      style={containerStyle}
      onWheel={zoom.handleWheel}
      onPointerDown={zoom.handlePointerDown}
      onPointerMove={zoom.handlePointerMove}
      onPointerUp={zoom.handlePointerUp}
      onPointerCancel={zoom.handlePointerUp}
      onDoubleClick={zoom.handleDoubleClick}
      onTouchStart={zoom.handleTouchStart}
      onTouchEnd={zoom.handleTouchEnd}
    >
      <img
        src={blobUrl}
        alt=""
        className={styles.previewImage}
        draggable={false}
      />
    </div>
  );
}
